import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import {
  Upload, FileText, Image, File, Trash2, Download, ExternalLink,
  FolderOpen, Plus, Check, Edit2, AlertTriangle, FileSpreadsheet, FileIcon,
  ChevronDown, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const CATEGORIES = ['KYC', 'Property Document', 'Agreement', 'Payment', 'Booking', 'Brochure', 'Price List', 'Other'];

const getFileIcon = (type) => {
  if (!type) return File;
  if (type.includes('pdf')) return FileText;
  if (type.includes('image')) return Image;
  if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return FileSpreadsheet;
  return FileIcon;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const DEAL_CHECKLIST = [
  { category: 'KYC', items: ['Aadhaar', 'PAN'] },
  { category: 'Booking', items: ['Booking Form', 'Token Receipt'] },
  { category: 'Agreement', items: ['Agreement'] },
  { category: 'Payment', items: ['Payment Receipt'] },
];

const EntityDocuments = ({ entityType, entityId, entityName, showChecklist = false }) => {
  const [documents, setDocuments] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveFolder, setDriveFolder] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [expanded, setExpanded] = useState(true);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const [statusRes, docsRes] = await Promise.all([
        api.get('/drive/status'),
        api.get(`/drive/documents?entity_type=${entityType}&entity_id=${entityId}`),
      ]);
      setDriveConnected(statusRes.data.connected);
      setDocuments(docsRes.data);
    } catch { /* silent */ }
    try {
      const folderRes = await api.get(`/drive/entity-folder/${entityType}/${entityId}`);
      setDriveFolder(folderRes.data);
    } catch { /* silent */ }
  }, [entityType, entityId]);

  useEffect(() => { if (entityId) fetchDocs(); }, [entityId, fetchDocs]);

  const doUpload = async (file, category, forceNew = false) => {
    if (!driveConnected) { toast.error('Google Drive not connected'); return; }

    // Check for duplicate name
    if (!forceNew) {
      const existing = documents.find(d => d.file_name === file.name && d.category === category);
      if (existing) {
        setDuplicateFile({ file, category, existingDoc: existing });
        setShowDuplicate(true);
        return;
      }
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/drive/upload?category=${encodeURIComponent(category)}&entity_type=${entityType}&entity_id=${entityId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${file.name} uploaded`);
      setShowUpload(false);
      fetchDocs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const file of files) doUpload(file, uploadCategory);
    e.target.value = '';
  };

  const handleDuplicateAction = (action) => {
    if (!duplicateFile) return;
    if (action === 'new_version') {
      doUpload(duplicateFile.file, duplicateFile.category, true);
    } else if (action === 'rename') {
      const ext = duplicateFile.file.name.includes('.') ? '.' + duplicateFile.file.name.split('.').pop() : '';
      const baseName = duplicateFile.file.name.replace(ext, '');
      const newName = `${baseName} (${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})${ext}`;
      const renamedFile = new File([duplicateFile.file], newName, { type: duplicateFile.file.type });
      doUpload(renamedFile, duplicateFile.category, true);
    }
    setShowDuplicate(false);
    setDuplicateFile(null);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
    try {
      await api.delete(`/drive/documents/${doc.id}`);
      toast.success('Deleted');
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (e) { toast.error('Failed'); }
  };

  // Group documents by category
  const grouped = {};
  documents.forEach(d => {
    const cat = d.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  // Checklist computation
  const checklistStatus = showChecklist ? DEAL_CHECKLIST.map(section => {
    const sectionDocs = documents.filter(d => d.category === section.category);
    return {
      ...section,
      items: section.items.map(item => {
        const itemLower = item.toLowerCase();
        const found = sectionDocs.some(d => {
          const nameLower = d.file_name.toLowerCase().replace(/\.[^.]+$/, '');
          return nameLower === itemLower || nameLower.startsWith(itemLower + ' ') || nameLower.startsWith(itemLower + '_') || nameLower.startsWith(itemLower + '-') || nameLower.endsWith(' ' + itemLower);
        });
        return { name: item, found };
      }),
    };
  }) : [];
  const totalCheckItems = checklistStatus.reduce((s, c) => s + c.items.length, 0);
  const completedItems = checklistStatus.reduce((s, c) => s + c.items.filter(i => i.found).length, 0);
  const missingItems = checklistStatus.flatMap(c => c.items.filter(i => !i.found).map(i => `${c.category}: ${i.name}`));

  return (
    <div className="space-y-3" data-testid={`entity-docs-${entityType}-${entityId}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <button className="flex items-center gap-2 font-semibold text-slate-900" onClick={() => setExpanded(!expanded)}>
          <FolderOpen className="w-4 h-4 text-slate-400" />
          Documents
          <span className="text-xs font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">{documents.length}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        </button>
        <div className="flex items-center gap-1.5">
          {driveFolder?.has_folder && (
            <a href={driveFolder.folder_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5" data-testid="open-drive-folder">
              <ExternalLink className="w-3 h-3" /> Drive
            </a>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowUpload(true)} disabled={!driveConnected} data-testid="entity-upload-btn">
            <Plus className="w-3 h-3 mr-1" /> Upload
          </Button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Missing Document Alert */}
          {showChecklist && missingItems.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="missing-docs-alert">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">
                  Documents incomplete ({completedItems}/{totalCheckItems})
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Missing: {missingItems.join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Checklist (deals) */}
          {showChecklist && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid="doc-checklist">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Document Checklist</p>
              {checklistStatus.map(section => (
                <div key={section.category}>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1">{section.category}</p>
                  <div className="space-y-0.5">
                    {section.items.map(item => (
                      <div key={item.name} className="flex items-center gap-2 text-xs">
                        {item.found ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded border border-slate-300" />
                        )}
                        <span className={item.found ? 'text-slate-600' : 'text-slate-400'}>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Document List grouped by category */}
          {documents.length === 0 ? (
            <div className="text-center py-6">
              <FolderOpen className="w-8 h-8 mx-auto text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">{driveConnected ? 'No documents yet' : 'Connect Google Drive to manage documents'}</p>
            </div>
          ) : (
            Object.entries(grouped).map(([cat, docs]) => (
              <div key={cat}>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{cat}</p>
                <div className="space-y-1.5">
                  {docs.map(doc => {
                    const Icon = getFileIcon(doc.file_type);
                    return (
                      <div key={doc.id} className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group" data-testid={`entity-doc-${doc.id}`}>
                        <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">{doc.file_name}</p>
                          <p className="text-[10px] text-slate-400">{doc.uploaded_by_name} · {formatDate(doc.created_at)}{doc.file_size ? ` · ${formatSize(doc.file_size)}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {doc.google_drive_url && (
                            <a href={doc.google_drive_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-white" title="Open in Drive">
                              <ExternalLink className="w-3 h-3 text-blue-500" />
                            </a>
                          )}
                          <button className="p-1 rounded hover:bg-red-50" onClick={() => handleDelete(doc)} title="Delete">
                            <Trash2 className="w-3 h-3 text-slate-300 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {/* Document Summary */}
          {documents.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.entries(grouped).map(([cat, docs]) => (
                <span key={cat} className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                  {cat}: {docs.length}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader><DialogTitle>Upload to {entityName || entityType}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-7 h-7 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-600">{uploading ? 'Uploading...' : 'Click to select files'}</p>
              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, DOC, XLS — Max 50MB</p>
              <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.pptx,.txt"
                onChange={handleFileSelect} data-testid="entity-file-input" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate File Dialog */}
      <Dialog open={showDuplicate} onOpenChange={v => { if (!v) { setShowDuplicate(false); setDuplicateFile(null); } }}>
        <DialogContent className="max-w-sm rounded-xl" data-testid="duplicate-dialog">
          <DialogHeader><DialogTitle>Document already exists</DialogTitle></DialogHeader>
          {duplicateFile && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                <span className="font-semibold">{duplicateFile.file.name}</span> already exists in {duplicateFile.category}.
              </p>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => handleDuplicateAction('new_version')} data-testid="upload-new-version">
                  <Upload className="w-4 h-4 mr-2" /> Upload as new version
                </Button>
                <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => handleDuplicateAction('rename')}>
                  <Edit2 className="w-4 h-4 mr-2" /> Rename new file
                </Button>
                <Button variant="ghost" className="w-full justify-start h-10 text-sm text-slate-500" onClick={() => { setShowDuplicate(false); setDuplicateFile(null); }}>
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntityDocuments;
